import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import LineItem2 from './LineItem2';
import CustomerForm from './CustomerForm';
import { EMPLOYEE_ROLES } from '../constants/employeeRoles';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { enrichCustomerDocForCreate } from '../utils/customerPortal';

const ModalContainer = styled.div`
  padding: 30px;
  max-width: 90vw;
  width: 1400px;
  max-height: 90vh;
  overflow-y: auto;
  background: var(--al-bg);
  color: white;
  font-family: 'Montserrat', sans-serif;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--al-bg);
    border-radius: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 0;
    
    &:hover {
      background: #5a5a5a;
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
  background: ${props => props.theme.colors.navbar};
`;

const Section = styled.div`
  background: ${props => props.theme.colors.navbar};
  border: none;
  border-radius: 0;
  padding: 20px;
  margin-bottom: 20px;
`;

const LinesSection = styled(Section)`
  padding-top: 12px;
  max-height: calc(70vh - 260px);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--al-bg);
    border-radius: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 0;
    
    &:hover {
      background: #5a5a5a;
    }
  }
`;

const CustomerInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.8rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  background: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.border};
  color: black;
  border-radius: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  text-transform: uppercase;
  
  option {
    background: white;
    color: black;
  }
`;

const DateInput = styled(Input)`
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

const NotesBox = styled.textarea`
  width: 100%;
  height: 100px;
  padding: 12px;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  resize: none;
  text-transform: uppercase;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 0;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.secondary ? `
    background: #6c757d;
    color: white;
    &:hover {
      background: #5a6268;
    }
  ` : props.danger ? `
    background: #dc3545;
    color: white;
    &:hover {
      background: #c82333;
    }
  ` : `
    background: ${props.theme.colors.primary};
    color: black;
    &:hover {
      background: ${props.theme.colors.primaryDark};
    }
  `}
`;

const ConfidenceIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: ${props => {
    const confidence = props.confidence * 100;
    if (confidence >= 90) return 'var(--al-primary)';
    if (confidence >= 70) return '#FFD700';
    return '#ff4444';
  }};
  
  &::before {
    content: '${props => {
      const confidence = props.confidence * 100;
      if (confidence >= 90) return '🟢';
      if (confidence >= 70) return '🟡';
      return '🔴';
    }}';
  }
`;

const ProductMatchIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
  color: ${props => {
    const confidence = props.confidence * 100;
    if (confidence >= 90) return 'var(--al-primary)';
    if (confidence >= 70) return '#FFD700';
    return '#ff4444';
  }};
  margin-top: 4px;
  
  &::before {
    content: '${props => {
      const confidence = props.confidence * 100;
      if (confidence >= 90) return '✅';
      if (confidence >= 70) return '⚠️';
      return '❌';
    }}';
  }
`;

const OrderIntakeForm = ({ parsedOrder, onClose, onApprove, onReject }) => {
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerStatus, setCustomerStatus] = useState(null);

  // Debug logging
  useEffect(() => {
    console.log('OrderIntakeForm received data:', {
      parsedOrder,
      customerName: parsedOrder?.parsedOrder?.customerName,
      lines: parsedOrder?.parsedOrder?.lines,
      fullStructure: parsedOrder
    });

    // Set customer status based on parsed order data
    if (parsedOrder?.parsedOrder?.customerWasCreated !== undefined) {
      setCustomerStatus(parsedOrder.parsedOrder.customerWasCreated ? 'created' : 'found');
    } else if (parsedOrder?.parsedOrder?.customerId) {
      setCustomerStatus('found');
    } else {
      setCustomerStatus('not_found');
    }
  }, [parsedOrder]);
  const [orderData, setOrderData] = useState({
    customerName: parsedOrder?.parsedOrder?.customerName || '',
    customerEmail: parsedOrder?.parsedOrder?.customerEmail || '',
    customerPhone: parsedOrder?.parsedOrder?.customerPhone || '',
    customerOrganization: parsedOrder?.parsedOrder?.customerOrganization || '',
    projectManager: parsedOrder?.parsedOrder?.projectManager || '',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: parsedOrder?.parsedOrder?.dueDate || '',
    notes: parsedOrder?.parsedOrder?.notes || '',
    isRush: parsedOrder?.parsedOrder?.isRush || false
  });

  const [lines, setLines] = useState(() => {
    if (parsedOrder?.parsedOrder?.lines?.length > 0) {
      return parsedOrder.parsedOrder.lines.map(line => ({
        isExpanded: true,
        id: Math.random().toString(36).substr(2, 9),
        design: line.designName || '',
        style: line.matchedProduct?.styleNo || line.garmentId || '',
        color: line.garmentColor || '',
        sizeType: line.sizeType || 'ADULT',
        sizes: line.sizes || {},
        frontInkColors: line.frontInkColors || [],
        backInkColors: line.backInkColors || [],
        otherInkColors: line.otherInkColors || [],
        printLocations: line.printLocations || [],
        price: line.pricePerPiece || '',
        cogs: line.matchedProduct?.cogs || line.cogs || '',
        // Preserve any existing artwork/files fields so LineItem2 can build on them
        files: line.files || undefined,
        designFileUrl: line.designFileUrl || '',
        matchedProduct: line.matchedProduct,
        matchingConfidence: line.matchingConfidence,
        matchingReason: line.matchingReason
      }));
    }
    return [{
      isExpanded: true,
      id: Math.random().toString(36).substr(2, 9),
      design: '',
      style: '',
      color: '',
      sizeType: 'ADULT',
      sizes: {},
      frontInkColors: [],
      backInkColors: [],
      otherInkColors: [],
      printLocations: [],
      price: '',
      cogs: ''
    }];
  });

  const handleLineUpdate = (index, updatedLine) => {
    setLines(prevLines => {
      const newLines = [...prevLines];
      newLines[index] = { ...newLines[index], ...updatedLine };
      return newLines;
    });
  };

  const handleAddCustomer = async (customerData) => {
    try {
      console.log('Adding customer to database:', customerData);
      
      const customerDoc = {
        client: customerData.client || orderData.customerOrganization,
        contact: customerData.contact || orderData.customerName,
        email: customerData.email || orderData.customerEmail,
        phone: customerData.phone || orderData.customerPhone,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: 'AI_PARSER_MANUAL'
      };

      const docRef = await addDoc(collection(db, 'customers'), enrichCustomerDocForCreate(customerDoc));
      console.log('Customer added with ID:', docRef.id);
      
      // Update the order data with the new customer ID
      setOrderData(prev => ({
        ...prev,
        customerId: docRef.id
      }));
      
      setCustomerStatus('created');
      setShowCustomerForm(false);
      
      // Show success message
      alert('Customer added successfully!');
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Error adding customer. Please try again.');
    }
  };

  const handleApprove = () => {
    onApprove({
      ...orderData,
      lines,
      // Include original AI order data for reference
      originalAiOrder: parsedOrder
    });
  };

  return (
    <ModalContainer>
      <HeaderSection>
        <CustomerInfo>
          <ConfidenceIndicator confidence={parsedOrder?.confidence?.customer || 0}>
            Customer Confidence: {((parsedOrder?.confidence?.customer || 0) * 100).toFixed(0)}%
          </ConfidenceIndicator>
                  {/* Customer Status Indicator */}
                  <div style={{ 
                    fontSize: '0.7rem', 
                    color: customerStatus === 'created' ? '#FFD700' : 
                           customerStatus === 'found' ? 'var(--al-primary)' : '#ff4444',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {customerStatus === 'created' && '🆕 New Customer Created'}
                    {customerStatus === 'found' && '✅ Existing Customer Found'}
                    {customerStatus === 'not_found' && '❌ Customer Not Found'}
                    
                    {customerStatus === 'not_found' && (
                      <button
                        onClick={() => setShowCustomerForm(true)}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '2px 8px',
                          borderRadius: '3px',
                          fontSize: '0.6rem',
                          cursor: 'pointer'
                        }}
                      >
                        Add to Database
                      </button>
                    )}
                  </div>
          {parsedOrder?.projectManager && (
            <div style={{ 
              fontSize: '0.7rem', 
              color: 'var(--al-primary)',
              marginTop: '4px'
            }}>
              👤 PM Auto-assigned: {parsedOrder.projectManager}
            </div>
          )}
          <Input
            placeholder="Contact Name"
            value={orderData.customerName}
            onChange={e => setOrderData({ ...orderData, customerName: e.target.value })}
          />
          <Input
            placeholder="Email"
            value={orderData.customerEmail}
            onChange={e => setOrderData({ ...orderData, customerEmail: e.target.value })}
          />
          <Input
            placeholder="Phone"
            value={orderData.customerPhone}
            onChange={e => setOrderData({ ...orderData, customerPhone: e.target.value })}
          />
          <Input
            placeholder="Client/Company"
            value={orderData.customerOrganization}
            onChange={e => setOrderData({ ...orderData, customerOrganization: e.target.value })}
          />
        </CustomerInfo>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '300px' }}>
          <Select
            value={orderData.projectManager}
            onChange={e => setOrderData({ ...orderData, projectManager: e.target.value })}
          >
            <option value="">SELECT PM</option>
            <option value="Blake">Blake</option>
            <option value="Zach">Zach</option>
            <option value="Uciel">Uciel</option>
            <option value="Ben">Ben</option>
          </Select>

          <DateInput
            type="date"
            value={orderData.startDate}
            onChange={e => setOrderData({ ...orderData, startDate: e.target.value })}
          />

          <DateInput
            type="date"
            value={orderData.dueDate}
            onChange={e => setOrderData({ ...orderData, dueDate: e.target.value })}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={orderData.isRush}
              onChange={e => setOrderData({ ...orderData, isRush: e.target.checked })}
            />
            Rush Order
          </label>
        </div>
      </HeaderSection>

      <LinesSection>
        {lines.map((line, index) => (
          <div key={line.id}>
            {line.matchedProduct && (
              <ProductMatchIndicator confidence={line.matchingConfidence}>
                Product Match: {line.matchedProduct.brand} {line.matchedProduct.styleNo} 
                ({line.matchingConfidence * 100}% - {line.matchingReason})
              </ProductMatchIndicator>
            )}
            <LineItem2
              lineData={line}
              index={index}
              isExpanded={line.isExpanded}
              onUpdate={(updatedLine) => handleLineUpdate(index, updatedLine)}
            />
          </div>
        ))}
      </LinesSection>

      <Section>
        <NotesBox
          value={orderData.notes}
          onChange={e => setOrderData({ ...orderData, notes: e.target.value })}
          placeholder="Order Notes..."
        />
      </Section>

      <ButtonGroup>
        <Button secondary onClick={onClose}>
          Close
        </Button>
        <Button danger onClick={() => onReject(parsedOrder.id)}>
          Reject
        </Button>
        <Button onClick={handleApprove}>
          Approve & Create Order
        </Button>
      </ButtonGroup>

      {/* Customer Form Modal */}
      {showCustomerForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'var(--al-bg)',
            padding: '30px',
            borderRadius: '4px',
            width: '500px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              color: 'white',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '24px',
              marginBottom: '20px',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}>
              Add Customer to Database
            </h2>
            <CustomerForm
              onSubmit={handleAddCustomer}
              onCancel={() => setShowCustomerForm(false)}
              initialData={{
                client: orderData.customerOrganization,
                contact: orderData.customerName,
                email: orderData.customerEmail,
                phone: orderData.customerPhone
              }}
              submitButtonText="Add Customer"
            />
          </div>
        </div>
      )}
    </ModalContainer>
  );
};

export default OrderIntakeForm;